import React from "react"
import { useSnackbar } from "notistack"
import { RoboComercial, RoboConvites } from "../../../services/apiRobo"
import { erroMsg } from "../components/comum"

const ESTAGIOS = [
    "aguardando_atribuicao",
    "novo",
    "em_convite",
    "no_grupo",
    "qualificando",
    "dor_identificada",
    "quente",
    "demonstracao_agendada",
    "fechado",
    "perdido",
]
const ROTULO: Record<string, string> = {
    aguardando_atribuicao: "Aguardando",
    novo: "Novo",
    em_convite: "Em convite",
    no_grupo: "No grupo",
    qualificando: "Qualificando",
    dor_identificada: "Dor identificada",
    quente: "Quente",
    demonstracao_agendada: "Demonstração",
    fechado: "Fechado",
    perdido: "Perdido",
}

export function Kanban() {
    const { enqueueSnackbar } = useSnackbar()
    const [counts, setCounts] = React.useState<Record<string, number>>({})
    const [colunas, setColunas] = React.useState<Record<string, any[]>>({})
    const [carregando, setCarregando] = React.useState(false)
    const [lead, setLead] = React.useState<any>(null)
    const [bulk, setBulk] = React.useState(false)

    const carregar = React.useCallback(async () => {
        setCarregando(true)
        try {
            const k = await RoboComercial.kanban()
            setCounts(k?.countsByStage || {})
            const entradas = await Promise.all(
                ESTAGIOS.map(est =>
                    RoboComercial.coluna({ estagio: est, limite: 25 })
                        .then(r => [est, r?.items || []] as [string, any[]])
                        .catch(() => [est, []] as [string, any[]])
                )
            )
            const map: Record<string, any[]> = {}
            entradas.forEach(([est, items]) => (map[est] = items))
            setColunas(map)
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    return (
        <section className="panel active" style={{ display: "block" }}>
            <div className="panel-actions">
                <h3 style={{ margin: 0 }}>📊 Funil de vendas</h3>
                <button className="ghost" onClick={() => setBulk(true)} title="Envio em massa pela fila oficial (substitui o bulk do WhatsApp Web)">
                    ✉ Mensagem em massa
                </button>
                <button className="ghost" onClick={carregar}>
                    ↻ Atualizar
                </button>
            </div>
            {carregando && <div className="muted small" style={{ padding: 12 }}>Carregando…</div>}
            <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: 12 }}>
                {ESTAGIOS.map(est => {
                    const lista = colunas[est] || []
                    return (
                        <div
                            key={est}
                            style={{
                                minWidth: 220,
                                maxWidth: 220,
                                background: "var(--panel-2)",
                                borderRadius: "var(--radius)",
                                padding: 8,
                            }}
                        >
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                                {ROTULO[est]} ({counts[est] ?? lista.length})
                            </div>
                            {lista.length === 0 && <div className="muted small">—</div>}
                            {lista.map(l => (
                                <div
                                    key={l.id}
                                    onClick={() => setLead(l)}
                                    style={{
                                        background: "var(--panel)",
                                        border: "1px solid var(--border)",
                                        borderRadius: 6,
                                        padding: 8,
                                        marginBottom: 6,
                                        cursor: "pointer",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                                        {l.nome || l.waId || l.telefone || "—"}
                                    </div>
                                    <div className="muted small">
                                        {[l.cidade, l.uf].filter(Boolean).join("/") || "sem cidade"}
                                    </div>
                                    <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                        {l.concorrente ? <span className="chip">{l.concorrente}</span> : null}
                                        {l.autoRespostaAtiva ? <span className="chip op">IA on</span> : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                })}
            </div>
            {lead && (
                <LeadModal
                    lead={lead}
                    aoFechar={() => setLead(null)}
                    aoMudar={() => {
                        setLead(null)
                        carregar()
                    }}
                />
            )}
            {bulk && <BulkMensagem colunas={colunas} aoFechar={() => setBulk(false)} />}
        </section>
    )
}

function BulkMensagem({ colunas, aoFechar }: { colunas: Record<string, any[]>; aoFechar: () => void }) {
    const { enqueueSnackbar } = useSnackbar()
    const [estagio, setEstagio] = React.useState("")
    const [texto, setTexto] = React.useState("")
    const [intervalo, setIntervalo] = React.useState(20)
    const [enviando, setEnviando] = React.useState(false)

    const leadIds = React.useMemo(() => {
        const alvos = estagio ? colunas[estagio] || [] : Object.values(colunas).flat()
        return alvos.map((l: any) => l.id).filter(Boolean)
    }, [colunas, estagio])

    async function criar() {
        if (!texto.trim() || leadIds.length === 0) {
            enqueueSnackbar("Texto e ao menos um lead.", { variant: "warning" })
            return
        }
        setEnviando(true)
        try {
            const r = await RoboComercial.criarLote({
                tipo: "MESSAGE",
                descricao: "Mensagem em massa (funil)",
                parametros: { leadIds, texto: texto.trim() },
                intervaloMs: Math.max(0, intervalo) * 1000,
            })
            enqueueSnackbar(`Lote criado: ${r?.total ?? leadIds.length} leads, paceado.`, { variant: "success" })
            aoFechar()
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setEnviando(false)
        }
    }

    return (
        <div className="modal" style={{ display: "flex" }}>
            <div className="modal-content" style={{ maxWidth: 520 }}>
                <div className="modal-header">
                    <strong>✉ Mensagem em massa</strong>
                    <button className="ghost-icon-btn" onClick={aoFechar}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    <div className="small muted" style={{ marginBottom: 8 }}>
                        Envia pela fila oficial (respeita janela de 24h e ritmo). Equivalente ao envio em massa antigo, agora pela API oficial.
                    </div>
                    <label className="small muted" style={{ display: "block", marginBottom: 8 }}>
                        Alvo
                        <select value={estagio} onChange={e => setEstagio(e.target.value)} style={{ width: "100%" }}>
                            <option value="">Todos os leads carregados</option>
                            {ESTAGIOS.map(s => (
                                <option key={s} value={s}>
                                    Estágio: {ROTULO[s]}
                                </option>
                            ))}
                        </select>
                    </label>
                    <textarea
                        rows={4}
                        placeholder="Mensagem para cada lead…"
                        value={texto}
                        onChange={e => setTexto(e.target.value)}
                        style={{ width: "100%" }}
                    />
                    <label className="small muted" style={{ display: "block", marginTop: 8 }}>
                        Intervalo entre envios (s)
                        <input type="number" min={0} value={intervalo} onChange={e => setIntervalo(Number(e.target.value))} style={{ width: 80, marginLeft: 6 }} />
                    </label>
                    <div className="small" style={{ marginTop: 6 }}>
                        {leadIds.length} lead(s) no alvo.
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="ghost" onClick={aoFechar}>
                        Cancelar
                    </button>
                    <button disabled={enviando || leadIds.length === 0 || !texto.trim()} onClick={criar}>
                        {enviando ? "Criando…" : "Criar envio"}
                    </button>
                </div>
            </div>
        </div>
    )
}

function LeadModal({ lead, aoFechar, aoMudar }: { lead: any; aoFechar: () => void; aoMudar: () => void }) {
    const { enqueueSnackbar } = useSnackbar()
    const [estagio, setEstagio] = React.useState(lead.estagio || "novo")

    async function acao(fn: () => Promise<any>, ok: string, fecha = true) {
        try {
            await fn()
            enqueueSnackbar(ok, { variant: "success" })
            if (fecha) aoMudar()
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    return (
        <div className="modal" style={{ display: "flex" }}>
            <div className="modal-content" style={{ maxWidth: 520 }}>
                <div className="modal-header">
                    <strong>{lead.nome || lead.waId || "Lead"}</strong>
                    <button className="ghost-icon-btn" onClick={aoFechar}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    <div className="small" style={{ marginBottom: 8 }}>
                        <div>
                            <strong>Telefone:</strong> {lead.telefone || lead.waId || "—"}
                        </div>
                        <div>
                            <strong>Cidade:</strong> {[lead.cidade, lead.uf].filter(Boolean).join("/") || "—"}
                        </div>
                        {lead.concorrente ? (
                            <div>
                                <strong>Sistema atual:</strong> {lead.concorrente}
                            </div>
                        ) : null}
                        {lead.resumoIa ? (
                            <div style={{ marginTop: 6 }}>
                                <strong>Resumo IA:</strong> {lead.resumoIa}
                            </div>
                        ) : null}
                    </div>

                    <label className="small muted">
                        Estágio
                        <select value={estagio} onChange={e => setEstagio(e.target.value)} style={{ width: "100%" }}>
                            {ESTAGIOS.map(s => (
                                <option key={s} value={s}>
                                    {ROTULO[s]}
                                </option>
                            ))}
                        </select>
                    </label>

                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                        <button className="ghost" onClick={() => acao(() => RoboComercial.assumir(lead.id), "Assumido.")}>
                            Assumir
                        </button>
                        <button className="ghost" onClick={() => acao(() => RoboComercial.liberar(lead.id), "Liberado.")}>
                            Liberar
                        </button>
                        <button
                            className="ghost"
                            onClick={() => acao(() => RoboComercial.autoResposta(lead.id, !lead.autoRespostaAtiva), "IA alternada.")}
                        >
                            {lead.autoRespostaAtiva ? "Desligar IA" : "Ligar IA"}
                        </button>
                        <button className="ghost" onClick={() => acao(() => RoboComercial.enriquecerPlaces(lead.id), "Places.")}>
                            Places
                        </button>
                        <button className="ghost" onClick={() => acao(() => RoboConvites.disparar(lead.id), "Convite disparado.")}>
                            Disparar convite
                        </button>
                        <button className="ghost" onClick={() => acao(() => RoboComercial.promoverGestor(lead.id), "Promovido a gestor.")}>
                            Promover a gestor
                        </button>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="ghost warning" onClick={() => acao(() => RoboComercial.removerLead(lead.id), "Removido.")}>
                        Remover
                    </button>
                    <span style={{ flex: 1 }} />
                    <button className="ghost" onClick={aoFechar}>
                        Fechar
                    </button>
                    <button
                        disabled={estagio === lead.estagio}
                        onClick={() => acao(() => RoboComercial.moverEstagio(lead.id, estagio), "Estágio atualizado.")}
                    >
                        Salvar estágio
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Kanban
